import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../config/supabase';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState(null);

  useEffect(() => {
    let mounted = true;
    let isInitializing = true;
    
    const { data: authListener } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔔 Auth event:', event, 'Initializing:', isInitializing);
      
      if (!mounted) return;

      // Ignorar eventos durante inicialização
      if (isInitializing && event === 'SIGNED_IN') {
        console.log('⏭️ Ignorando SIGNED_IN durante inicialização');
        return;
      }

      if (event === 'SIGNED_IN' && session?.user) {
        console.log('✅ Login detectado, carregando perfil...');
        
        try {
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          console.log('📊 Resposta:', { profile, error });

          if (error) {
            console.error('❌ Erro:', error);
            setLoading(false);
            return;
          }

          if (profile) {
            if (profile.is_superadmin) {
              profile.role = 'superadmin';
            }
            console.log('✅ Perfil carregado:', profile);
            setUser(profile);
          }
          
          setLoading(false);
        } catch (err) {
          console.error('💥 Exceção:', err);
          setLoading(false);
        }
      } else if (event === 'SIGNED_OUT') {
        console.log('👋 Logout detectado');
        setUser(null);
        setViewMode(null);
        setLoading(false);
      }
    });

    async function init() {
      try {
        console.log('🚀 Iniciando verificação de sessão...');
        
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session?.user) {
          console.log('✅ Sessão existente encontrada');
          
          const { data: profile, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', session.user.id)
            .single();

          if (error) {
            console.error('❌ Erro ao carregar perfil:', error);
          } else if (profile) {
            if (profile.is_superadmin) {
              profile.role = 'superadmin';
            }
            console.log('✅ Perfil carregado:', profile);
            setUser(profile);
          }
        } else {
          console.log('ℹ️ Nenhuma sessão ativa');
        }
        
        if (mounted) {
          setLoading(false);
          isInitializing = false;
        }
      } catch (error) {
        console.error('💥 Erro ao inicializar:', error);
        if (mounted) {
          setLoading(false);
          isInitializing = false;
        }
      }
    }
    
    init();

    return () => {
      console.log('🧹 Cleanup');
      mounted = false;
      authListener?.subscription?.unsubscribe();
    };
  }, []);

  const login = useCallback(async (email, password) => {
    try {
      console.log('🔐 Tentando login...');
      setLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error('❌ Erro no login:', error);
        throw error;
      }

      console.log('✅ Login bem-sucedido');
      return data;
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    console.log('👋 Fazendo logout...');
    await supabase.auth.signOut();
    setUser(null);
    setViewMode(null);
  }, []);

  const switchViewMode = useCallback((mode) => {
    if (user?.role === 'superadmin') {
      console.log('🔄 Mudando modo para:', mode);
      setViewMode(mode);
    }
  }, [user]);

  const effectiveRole = useMemo(() => {
    if (user?.role === 'superadmin' && viewMode) {
      return viewMode;
    }
    return user?.role;
  }, [user, viewMode]);

  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    viewMode,
    switchViewMode,
    effectiveRole
  }), [user, loading, login, logout, viewMode, switchViewMode, effectiveRole]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
